import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { RefreshCcw, Trash2, Upload } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import PortfolioForm from './PortfolioForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioTab = () => {
  const [items, setItems] = useState([]);
  const [subcategories, setSubcategories] = useState({ business: [], event: [] });
  const [loading, setLoading] = useState(true);

  const auth = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/portfolio/`, { headers: auth() }),
        axios.get(`${API}/portfolio/subcategories`),
      ]);
      setItems(a.data.items || []);
      setSubcategories(b.data);
    } catch (err) {
      toast({ title: 'Failed to load showcase', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const patch = async (id, changes) => {
    try {
      await axios.patch(`${API}/portfolio/${id}`, changes, { headers: auth() });
      fetch();
    } catch (err) { toast({ title: 'Update failed', description: err.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the showcase?`)) return;
    try { await axios.delete(`${API}/portfolio/${it.id}`, { headers: auth() }); fetch(); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const replaceLabel = async (it, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('label', file);
    try {
      await axios.post(`${API}/portfolio/${it.id}/label`, fd, { headers: auth() });
      toast({ title: 'Label replaced', description: 'Bottle re-rendered' }); fetch();
    } catch (err) { toast({ title: 'Upload failed', description: err.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const active = items.filter((i) => i.active).length;

  return (
    <div className="space-y-4" data-testid="admin-portfolio-tab">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="text-sm text-gray-500">Showcase entries</div>
          <div className="text-2xl font-bold text-gray-900">{items.length} <span className="text-sm font-normal text-gray-500">({active} live on corp.thelokal.ca)</span></div>
        </div>
        <Button variant="outline" size="sm" onClick={fetch} disabled={loading} data-testid="portfolio-refresh">
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <PortfolioForm subcategories={subcategories} onSaved={fetch} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && <div className="text-gray-400 text-sm">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-gray-400 text-sm">No showcase entries yet — upload your first label above.</div>}
        {items.map((it) => (
          <div key={it.id} className={`bg-white rounded-lg border overflow-hidden ${it.active ? '' : 'opacity-60'}`} data-testid={`portfolio-item-${it.id}`}>
            <div className="aspect-[2/3] bg-[#f3e4d2]">
              {it.bottleUrl && <img src={`${BACKEND_URL}${it.bottleUrl}`} alt={it.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-3 space-y-2 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#C9A961] font-bold">{it.category === 'business' ? 'Business' : 'Wedding / Event'} · {it.subcategoryLabel}</div>
                <div className="font-semibold text-gray-900 truncate" title={it.name}>{it.name}</div>
                <div className="text-xs text-gray-500 truncate">{it.useCase || '—'} · {it.orientation}</div>
              </div>
              <div className="flex flex-wrap gap-2 items-center text-xs">
                <label className="flex items-center gap-1"><input type="checkbox" checked={it.active} onChange={(e) => patch(it.id, { active: e.target.checked })} data-testid={`portfolio-active-${it.id}`} /> Active</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={it.featured} onChange={(e) => patch(it.id, { featured: e.target.checked })} data-testid={`portfolio-featured-${it.id}`} /> Featured</label>
                <select value={it.bottle} onChange={(e) => patch(it.id, { bottle: e.target.value })} className="border rounded px-1 py-0.5" data-testid={`portfolio-bottle-${it.id}`}>
                  <option value="dark">Dark</option><option value="clear">Clear</option>
                </select>
                <input type="number" value={it.displayOrder} onChange={(e) => patch(it.id, { displayOrder: Number(e.target.value) })} className="border rounded px-1 py-0.5 w-14" title="Display order" data-testid={`portfolio-order-${it.id}`} />
              </div>
              <div className="flex justify-between items-center">
                <label className="inline-flex items-center gap-1 text-xs text-[#722F37] cursor-pointer">
                  <Upload className="w-3 h-3" /> Replace label
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => replaceLabel(it, e.target.files?.[0])} />
                </label>
                <button onClick={() => remove(it)} className="text-xs text-red-600 inline-flex items-center gap-1" data-testid={`portfolio-delete-${it.id}`}>
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioTab;
